/** 1.26 browser/Flutter wire contract. Visibility is always enforced by the server. */
export type Visibility = 'private' | 'unlisted' | 'public';
export type Cadence = 'immediate' | 'daily' | 'weekly' | 'none';
export type Family =
  | 'mixes_packs'
  | 'collections_guides'
  | 'events_live'
  | 'creator_collaboration'
  | 'external_media'
  | 'saved_search_notification_budget';
export type ResourceKind =
  | 'mixes'
  | 'starter-packs'
  | 'collections'
  | 'guides'
  | 'events'
  | 'post-collaborations'
  | 'channels'
  | 'media/playlists'
  | 'saved-searches';
export type Role = 'owner' | 'editor' | 'viewer' | 'none';
export interface Person {
  id: string;
  handle: string;
  name: string;
}
export interface Source {
  postId: string;
  commentId?: string;
  title: string;
  href: string;
  available: boolean;
}
export interface MixRule {
  kind: 'account' | 'community' | 'topic';
  value: string;
}
export interface PackChoice {
  id: string;
  kind: 'account' | 'community' | 'mix';
  target: string;
  label: string;
  state: 'available' | 'applied' | 'requested' | 'unavailable';
}
export interface CollectionItem {
  id: string;
  source: Source;
  annotation: string;
}
export interface GuideBlock {
  id: string;
  kind: 'text' | 'source' | 'quote';
  text: string;
  source?: Source;
  consent: 'not_required' | 'pending' | 'accepted' | 'withdrawn';
  contributor?: Person;
}
export interface EventQuestion {
  id: string;
  author: Person;
  text: string;
  votes: number;
  voted: boolean;
  answer?: Source;
}
export interface Collaborator {
  person: Person;
  state: 'pending' | 'accepted' | 'declined' | 'withdrawn';
}
export interface MediaItem {
  comments?: { seconds: number; source: Source }[];
  id: string;
  url: string;
  title: string;
  provider: 'youtube' | 'twitch';
  chapters: { seconds: number; title: string }[];
  postId?: string;
  available: boolean;
}
export interface SearchFilters {
  query: string;
  author: string;
  community: string;
  topic: string;
  from: string;
  until: string;
  media: 'any' | 'image' | 'video' | 'link';
}
export interface Payloads {
  mixes: { rules: MixRule[]; mode: 'recent' | 'relevant' | 'catch_up' };
  'starter-packs': { choices: PackChoice[] };
  collections: {
    items: CollectionItem[];
    members: { person: Person; role: 'editor' | 'viewer' }[];
  };
  guides: { blocks: GuideBlock[]; state: 'draft' | 'published'; collectionId?: string };
  events: {
    kind: 'text_ama' | 'external_live';
    startsAt: string;
    endsAt: string;
    timezone: string;
    community: string;
    state: 'scheduled' | 'live' | 'ended' | 'cancelled';
    providerUrl: string;
    replayUrl: string;
    recap: string;
    questions: EventQuestion[];
  };
  'post-collaborations': {
    body: string;
    collaborators: Collaborator[];
    state: 'draft' | 'published';
    postId?: string;
    proposals: {
      id: string;
      body: string;
      author: Person;
      state: 'pending' | 'accepted' | 'rejected';
    }[];
  };
  channels: { cadence: Cadence; postIds: string[] };
  'media/playlists': { items: MediaItem[] };
  'saved-searches': { filters: SearchFilters; cadence: Cadence };
}
export interface Resource<K extends ResourceKind = ResourceKind> {
  id: string;
  slug: string;
  kind: K;
  title: string;
  description: string;
  visibility: Visibility;
  owner: Person;
  role: Role;
  revision: number;
  updatedAt: string;
  data: Payloads[K];
  subscribed: boolean;
  pinned: boolean;
  rsvp: boolean;
  reminder: boolean;
}
export interface Page<K extends ResourceKind> {
  items: Resource<K>[];
  next: string | null;
}
export interface Capabilities {
  schema_version: 1;
  flags: Record<Family, boolean>;
}
export interface FeedItem {
  id: string;
  body: string;
  author: Person;
  href: string;
  createdAt: string;
}
export interface MixFeed {
  items: FeedItem[];
  next: string | null;
  snapshot: string;
  complete: boolean;
  total: number;
}
export interface DeliveryPreferences {
  dailyBudget: number;
  quietStart: string;
  quietEnd: string;
  timezone: string;
  digest: 'daily' | 'weekly' | 'none';
}
export interface Dashboard {
  postDrafts?: { id: string; body: string }[];
  drafts: Resource<'post-collaborations'>[];
  events: Resource<'events'>[];
  scheduled: { id: string; body: string; publishAt: string }[];
  metrics: {
    posts: number;
    replies: number;
    channelSubscribers: number;
    answeredQuestions: number;
  };
}
export const families: Record<ResourceKind, Family> = {
  mixes: 'mixes_packs',
  'starter-packs': 'mixes_packs',
  collections: 'collections_guides',
  guides: 'collections_guides',
  events: 'events_live',
  'post-collaborations': 'creator_collaboration',
  channels: 'creator_collaboration',
  'media/playlists': 'external_media',
  'saved-searches': 'saved_search_notification_budget',
};
export const labels: Record<ResourceKind, string> = {
  mixes: 'Signal Mixes',
  'starter-packs': 'Starter Packs',
  collections: 'Shared collections',
  guides: 'Guides',
  events: 'Events & AMAs',
  'post-collaborations': 'Collaborative drafts',
  channels: 'Creator channels',
  'media/playlists': 'Video playlists',
  'saved-searches': 'Saved searches',
};
export const paths: Record<ResourceKind, string> = {
  mixes: '/mixes',
  'starter-packs': '/starter-packs',
  collections: '/collections',
  guides: '/guides',
  events: '/events',
  'post-collaborations': '/collaborations',
  channels: '/channels',
  'media/playlists': '/playlists',
  'saved-searches': '/saved-searches',
};
export const resourceHref = (r: Pick<Resource, 'kind' | 'slug'>) =>
  `${paths[r.kind]}/${encodeURIComponent(r.slug)}`;
export const emptyFilters: SearchFilters = {
  query: '',
  author: '',
  community: '',
  topic: '',
  from: '',
  until: '',
  media: 'any',
};
export function initialPayload<K extends ResourceKind>(kind: K): Payloads[K] {
  const values: Payloads = {
    mixes: { rules: [], mode: 'recent' },
    'starter-packs': { choices: [] },
    collections: { items: [], members: [] },
    guides: { blocks: [], state: 'draft' },
    events: {
      kind: 'text_ama',
      startsAt: '',
      endsAt: '',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      community: '',
      state: 'scheduled',
      providerUrl: '',
      replayUrl: '',
      recap: '',
      questions: [],
    },
    'post-collaborations': { body: '', collaborators: [], state: 'draft', proposals: [] },
    channels: { cadence: 'none', postIds: [] },
    'media/playlists': { items: [] },
    'saved-searches': { filters: { ...emptyFilters }, cadence: 'none' },
  };
  return values[kind];
}
